/**
 * Base API client for Mews Booking Engine API
 * 
 * Handles authentication, request/response interceptors, and error handling
 */

import type { ApiResponse, MewsApiError } from './types';
import { retryWithBackoff, get408ErrorMessage } from './retry';

/**
 * Environment variable configuration
 */
interface MewsApiConfig {
  /** Base URL for Mews API (e.g., "https://api.mews.com") */
  baseUrl: string;
  /** Client identifier header value */
  clientIdentifier: string;
  /** Hotel ID for API calls */
  hotelId?: string;
  /** Configuration ID for booking engine */
  configurationId?: string;
}

/**
 * Validates and retrieves Mews API configuration from environment variables
 * 
 * @returns Validated configuration object
 * @throws Error if required environment variables are missing or invalid
 */
export function getMewsApiConfig(): MewsApiConfig {
  const baseUrl = import.meta.env.VITE_MEWS_API_BASE_URL;
  const clientIdentifier = import.meta.env.VITE_MEWS_CLIENT_IDENTIFIER;
  const hotelId = import.meta.env.VITE_MEWS_HOTEL_ID;
  const configurationId = import.meta.env.VITE_MEWS_CONFIGURATION_ID;

  // Validate required environment variables
  if (!baseUrl) {
    throw new Error(
      'Missing required environment variable: VITE_MEWS_API_BASE_URL. ' +
      'Please add it to your .env.local file.'
    );
  }

  if (!clientIdentifier) {
    throw new Error(
      'Missing required environment variable: VITE_MEWS_CLIENT_IDENTIFIER. ' +
      'Please add it to your .env.local file.'
    );
  }

  // Validate UUID format for optional IDs
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  
  if (hotelId && !uuidRegex.test(hotelId)) {
    console.warn(
      `⚠️  VITE_MEWS_HOTEL_ID does not appear to be a valid UUID: ${hotelId}`
    );
  }

  if (configurationId && !uuidRegex.test(configurationId)) {
    console.warn(
      `⚠️  VITE_MEWS_CONFIGURATION_ID does not appear to be a valid UUID: ${configurationId}`
    );
  }

  return {
    baseUrl: baseUrl.trim(),
    clientIdentifier: clientIdentifier.trim(),
    hotelId: hotelId?.trim(),
    configurationId: configurationId?.trim(),
  };
}

/**
 * Returns the Mews Applications base URL for card-authorization redirects (and optional future payment redirects).
 *
 * - If VITE_MEWS_APPLICATIONS_BASE_URL is set, returns it (trimmed).
 * - Else derives from VITE_MEWS_API_BASE_URL:
 *   - api.mews.com → https://app.mews.com
 *   - api.mews-demo.com → https://app.mews-demo.com
 *
 * @see STEP_4_4_OPTION_B_TASKMASTER.md - Task 2
 * @see environments.md - Demo / Production Mews applications base url
 */
export function getMewsApplicationsBaseUrl(): string {
  const envUrl = import.meta.env.VITE_MEWS_APPLICATIONS_BASE_URL;
  if (envUrl && typeof envUrl === 'string' && envUrl.trim()) {
    return envUrl.trim();
  }
  const apiBaseUrl = import.meta.env.VITE_MEWS_API_BASE_URL ?? '';
  if (apiBaseUrl.includes('mews-demo')) {
    return 'https://app.mews-demo.com';
  }
  return 'https://app.mews.com';
}

/**
 * Custom error class for Mews API errors
 */
export class MewsApiClientError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public code?: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'MewsApiClientError';
  }
}

/**
 * Parses error response from API
 * 
 * Extracts RequestId (or similar correlation IDs) from error Details when present
 * so we can surface it in logs and support tooling. This is an internal
 * observability feature and not a documented Booking Engine API guarantee.
 */
function parseErrorResponse(response: Response, body: unknown): MewsApiError {
  // Mews API might return errors in different formats
  if (body && typeof body === 'object') {
    // Check for standard error format
    if ('Message' in body) {
      const details = 'Details' in body ? (body.Details as Record<string, unknown>) : undefined;
      // Extract RequestId / requestId from Details if present for observability
      const requestId = details && typeof details.RequestId === 'string' 
        ? details.RequestId 
        : details && typeof details.requestId === 'string'
        ? details.requestId
        : undefined;
      
      return {
        Message: String(body.Message),
        Code: 'Code' in body ? String(body.Code) : undefined,
        Details: details,
        RequestId: requestId,
      };
    }
    
    // Check if the response itself is an error message (string in object)
    if ('error' in body) {
      const details = body as Record<string, unknown>;
      const requestId = typeof details.RequestId === 'string' 
        ? details.RequestId 
        : typeof details.requestId === 'string'
        ? details.requestId
        : undefined;
      
      return {
        Message: String(body.error),
        Code: 'code' in body ? String(body.code) : String(response.status),
        Details: body,
        RequestId: requestId,
      };
    }
    
    // Check for Mews-specific error format
    // Sometimes errors come as the response body directly
    const bodyStr = JSON.stringify(body);
    if (bodyStr.length < 500) { // If it's a short message, it might be an error
      return {
        Message: bodyStr,
        Code: String(response.status),
        Details: body,
      };
    }
  }

  // If body is a string, use it as the error message
  if (typeof body === 'string') {
    return {
      Message: body || response.statusText || 'An unknown error occurred',
      Code: String(response.status),
    };
  }

  // Fallback error message
  return {
    Message: response.statusText || 'An unknown error occurred',
    Code: String(response.status),
    Details: body ? { responseBody: body } : undefined,
  };
}

/**
 * Creates a standardized API response
 */
function createApiResponse<T>(
  data: T | undefined,
  error: MewsApiError | undefined,
  status: number | undefined
): ApiResponse<T> {
  return {
    data,
    error,
    status,
  };
}

/**
 * Base API client class
 */
class MewsApiClient {
  private config: MewsApiConfig;

  constructor(config?: MewsApiConfig) {
    this.config = config || getMewsApiConfig();
  }

  /**
   * Gets the base URL for API requests
   */
  getBaseUrl(): string {
    return this.config.baseUrl;
  }

  /**
   * Gets the hotel ID
   */
  getHotelId(): string | undefined {
    return this.config.hotelId;
  }

  /**
   * Gets the configuration ID
   */
  getConfigurationId(): string | undefined {
    return this.config.configurationId;
  }

  /**
   * Gets the client identifier
   */
  getClientIdentifier(): string {
    return this.config.clientIdentifier;
  }

  /**
   * Makes a GET request to the Mews API
   * 
   * @param endpoint - API endpoint path (e.g., "/api/distributor/v1/configuration")
   * @param params - Optional query parameters
   * @returns Promise resolving to API response
   */
  async get<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>
  ): Promise<ApiResponse<T>> {
    try {
      // Build URL with query parameters
      const url = new URL(endpoint, this.config.baseUrl);
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            url.searchParams.append(key, String(value));
          }
        });
      }

      // Make request with required headers
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-Identifier': this.config.clientIdentifier,
          'Accept': 'application/json',
        },
        // Add credentials for CORS if needed
        mode: 'cors',
      });

      // Parse response body
      let body: unknown;
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        body = await response.json();
      } else {
        body = await response.text();
      }

      // Handle error responses
      if (!response.ok) {
        const error = parseErrorResponse(response, body);
        return createApiResponse<T>(
          undefined,
          error,
          response.status
        );
      }

      // Return successful response
      return createApiResponse<T>(body as T, undefined, response.status);
    } catch (error) {
      // Handle network errors (CORS, network failures, etc.)
      let errorMessage = 'Network error: Failed to connect to Mews API';
      let errorCode = 'NETWORK_ERROR';
      let errorUrl = endpoint; // Fallback if url is not defined
      
      try {
        const url = new URL(endpoint, this.config.baseUrl);
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              url.searchParams.append(key, String(value));
            }
          });
        }
        errorUrl = url.toString();
      } catch {
        // URL construction failed, use endpoint as fallback
      }
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        // This is likely a CORS error or network failure
        errorMessage = `Failed to fetch from ${errorUrl}. This may be due to CORS restrictions or network connectivity. Check browser console for details.`;
        errorCode = 'FETCH_ERROR';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return createApiResponse<T>(
        undefined,
        {
          Message: errorMessage,
          Code: errorCode,
          Details: {
            url: errorUrl,
            endpoint: endpoint,
            error: error instanceof Error ? error.message : String(error),
          },
        },
        undefined
      );
    }
  }

  /**
   * Makes a POST request to the Mews API with retry logic
   * 
   * Implements retry logic for:
   * - 429 Too Many Requests: Uses Retry-After header or exponential backoff
   * - 500 Internal Server Error: Exponential backoff retry
   * - 408 Request Timeout: Returns error with guidance (not retried)
   * 
   * @param endpoint - API endpoint path
   * @param body - Request body data
   * @returns Promise resolving to API response
   */
  async post<T>(
    endpoint: string,
    body?: Record<string, unknown>
  ): Promise<ApiResponse<T>> {
    const url = new URL(endpoint, this.config.baseUrl);

    try {
      // Wrap fetch in retry logic
      const response = await retryWithBackoff(
        async () => {
          return await fetch(url.toString(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Client-Identifier': this.config.clientIdentifier,
              'Accept': 'application/json',
            },
            body: body ? JSON.stringify(body) : undefined,
            mode: 'cors',
          });
        },
        {
          maxRetries: 3,
          initialDelayMs: 1000,
          maxDelayMs: 30000,
          backoffMultiplier: 2,
          useRetryAfterHeader: true,
          shouldRetry: (status: number) => {
            // Retry on 429 (Too Many Requests) and 500 (Internal Server Error)
            // Do not retry on 408 (Request Timeout) - requires reducing request load
            return status === 429 || status === 500;
          },
        }
      );

      // Parse response body
      let responseBody: unknown;
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        responseBody = await response.json();
      } else {
        responseBody = await response.text();
      }

      // Log request/response for debugging (only in development)
      if (import.meta.env.DEV) {
        console.log('[Mews API] POST Request:', {
          url: url.toString(),
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Client-Identifier': this.config.clientIdentifier,
          },
          body: body,
        });
        console.log('[Mews API] Response:', {
          status: response.status,
          statusText: response.statusText,
          body: responseBody,
        });
      }

      // Handle error responses
      if (!response.ok) {
        const error = parseErrorResponse(response, responseBody);
        
        // Special handling for 408 Request Timeout
        if (response.status === 408) {
          // 408 errors indicate the request puts too much demand on the system
          // Provide guidance to reduce request load rather than retrying
          return createApiResponse<T>(
            undefined,
            {
              Message: get408ErrorMessage(),
              Code: '408',
              Details: {
                ...error.Details,
                guidance: 'Reduce request load by splitting requests into smaller time periods',
              },
              RequestId: error.RequestId,
            },
            response.status
          );
        }
        
        // Log RequestId for observability (internal convention only)
        if (error.RequestId && import.meta.env.DEV) {
          console.warn('[Mews API] Error RequestId:', error.RequestId, '- Include this when contacting MEWS support');
        }
        
        return createApiResponse<T>(
          undefined,
          error,
          response.status
        );
      }

      // Return successful response
      return createApiResponse<T>(responseBody as T, undefined, response.status);
    } catch (error) {
      // Handle network errors (CORS, network failures, etc.)
      let errorMessage = 'Network error: Failed to connect to Mews API';
      let errorCode = 'NETWORK_ERROR';
      let errorUrl = url.toString();
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        // This is likely a CORS error or network failure
        errorMessage = `Failed to fetch from ${errorUrl}. This may be due to CORS restrictions or network connectivity. Check browser console for details.`;
        errorCode = 'FETCH_ERROR';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return createApiResponse<T>(
        undefined,
        {
          Message: errorMessage,
          Code: errorCode,
          Details: {
            url: errorUrl,
            endpoint: endpoint,
            error: error instanceof Error ? error.message : String(error),
          },
        },
        undefined
      );
    }
  }
}

/**
 * Singleton instance of the API client
 */
let apiClientInstance: MewsApiClient | null = null;

/**
 * Gets or creates the API client instance
 * 
 * @param config - Optional configuration (uses environment variables if not provided)
 * @returns API client instance
 */
export function getApiClient(config?: MewsApiConfig): MewsApiClient {
  if (!apiClientInstance) {
    apiClientInstance = new MewsApiClient(config);
  }
  return apiClientInstance;
}

/**
 * Resets the API client instance (useful for testing)
 */
export function resetApiClient(): void {
  apiClientInstance = null;
}
